import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { supabase } from '../config/supabase';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';

const UsersContainer = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const UsersHeader = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleSection = styled.div``;

const UsersTitle = styled(TitleLarge)`
  color: ${colors.neutral.black};
  margin-bottom: 1rem;
`;

const ContentContainer = styled.div`
  width: 100%;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 1rem;
  gap: 2rem;
  cursor: pointer;
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const UserInfoImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 999px;
  flex-shrink: 0;
  margin-right: 12px;
`;

const UserLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  gap: 4px;
  flex: 1;
`;

const UserInfoSpan = styled.span`
  font-size: 12px;
  color: ${colors.neutral.charcoal};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.charcoal};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.charcoal};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.reds.bright};
`;

export default function Users() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    if (!user?.id || !isAuthenticated) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, verify the user session is still valid
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session expired');
      }

      // Step 1: Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        console.error('Error fetching creator details:', creatorError);
        if (creatorError.code === 'PGRST116') {
          throw new Error('No creator account found. Please contact support.');
        }
        throw new Error('Failed to verify creator account');
      }

      if (!creatorDetails) {
        throw new Error('No creator details found for user');
      }
      
      const creatorId = creatorDetails.id;

      // Step 2: Fetch users from EndUserDetails using the creator's ID
      const { data, error } = await supabase
        .from('EndUserDetails')
        .select('*')
        .eq('CreatorID', creatorId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users:', error);
        if (error.code === 'PGRST116') {
          setUsers([]);
        } else {
          throw new Error('Failed to fetch users');
        }
      } else {
        setUsers(data || []);
      }
    } catch (error) {
      console.error('Error in fetchUsers:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAuthenticated) {
      fetchUsers();
    } else if (!isAuthenticated) {
      setError('Please log in to view users');
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <UsersContainer>
        <UsersHeader>
          <HeaderContent>
            <TitleSection>
              <UsersTitle>Users</UsersTitle>
              <SubtitleMedium>Manage user accounts and permissions</SubtitleMedium>
            </TitleSection>
            <Button>Add User</Button>
          </HeaderContent>
        </UsersHeader>
        <ContentContainer>
          <ErrorMessage>Please log in to view users</ErrorMessage>
        </ContentContainer>
      </UsersContainer>
    );
  }

  return (
    <UsersContainer>
      <UsersHeader>
        <HeaderContent>
          <TitleSection>
            <UsersTitle>Users</UsersTitle>
            <SubtitleMedium>Manage user accounts and permissions</SubtitleMedium>
          </TitleSection>
          <Button>Add User</Button>
        </HeaderContent>
      </UsersHeader>
      <ContentContainer>
        {loading ? (
          <LoadingMessage>Loading users...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <UserList>
            {users.length === 0 ? (
              <EmptyState>No users found.</EmptyState>
            ) : (
              users.map(user => (
                <UserRow key={user.id} onClick={() => navigate(`/users/${user.id}`)}>
                  <UserInfo>
                    <UserInfoImage src={user.ProfilePicture || 'https://placehold.co/64'} alt="Profile Picture" />
                    <UserLabel><UserInfoSpan>Name</UserInfoSpan> {user.FirstName + ' ' + user.LastName || 'Unnamed User'}</UserLabel>
                    <UserLabel><UserInfoSpan>Email</UserInfoSpan> {user.email || 'No email'}</UserLabel>
                    <UserLabel><UserInfoSpan>Membership type</UserInfoSpan> {user.MembershipType + ' membership' || 'No membership'}</UserLabel>
                    <UserLabel><UserInfoSpan>Country</UserInfoSpan> {user.country || 'No country'}</UserLabel>
                    <UserLabel><UserInfoSpan>Created</UserInfoSpan> {new Date(user.created_at).toLocaleDateString()}</UserLabel>
                  </UserInfo>
                </UserRow>
              ))
            )}
          </UserList>
        )}
      </ContentContainer>
    </UsersContainer>
  );
} 